import { useRef, useState } from 'react';
import { useFormik } from 'formik';

import {
  Button,
  ButtonLink,
  CheckboxField,
  Col,
  FileField,
  FormMessage,
  Icon,
  PhoneField,
  RadioField,
  Row,
  Text,
  TextAreaField,
  TextField,
} from '../../common/components';
import { APP_URL, isDemo } from '../../common/constants/common';
import routes from '../../common/constants/routes';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { t } from '../../common/i18n';
import { selectSecurity } from '../../common/store/securitySlice';
import { setFlashMessage } from '../../common/store/uiSlice';
import Yup, { YupEmail, YupPhone } from '../../common/utils/FormValidation';
import { consentText, OFFERS_WITH_ATTACHMENTS } from '../config/catalogConfig';
import { createServiceLead } from '../services/ServiceCatalogService';
import { ServiceLeadFormValues, ServiceOffer, ServicePartner } from '../types';

const namespace = 'OtherServices';

interface ServiceLeadFormProps {
  partner: ServicePartner;
  /** Služba předvybraná z odkazu (`?offer=<slug>`); jinak první nabídka partnera. */
  preselectedOfferSlug?: string | null;
  /** Token náhledu nezveřejněného partnera – bez něj by BE poptávku na vypnutou nabídku odmítl (404). */
  preview?: string | null;
}

const validationSchema = Yup.object({
  contactName: Yup.string().trim().required(),
  contactEmail: YupEmail,
  contactPhone: YupPhone,
  consent: Yup.boolean().oneOf([true], t('Form.consentRequired', namespace)),
});

/**
 * Jeden sdílený poptávkový formulář pro všechny nabídky: jméno/telefon/e-mail předvyplněné
 * z profilu, dům připojený automaticky (jen read-only řádek), volitelná pole z nabídky
 * (`formFields`), přílohy a souhlas s předáním údajů. Po odeslání se místo formuláře ukáže
 * poděkování – uživatel nemá důvod posílat totéž dvakrát.
 */
export const ServiceLeadForm: React.FC<ServiceLeadFormProps> = ({ partner, preselectedOfferSlug = null, preview = null }) => {
  const dispatch = useAppDispatch();
  // Jeden formulář za partnera: uživatel si vybere, o kterou jeho službu má zájem (když jich je víc).
  const [selectedSlug, setSelectedSlug] = useState<string>(
    partner.offers.find((item) => item.slug === preselectedOfferSlug)?.slug ?? partner.offers[0]?.slug ?? ''
  );
  const offer: ServiceOffer | undefined = partner.offers.find((item) => item.slug === selectedSlug) ?? partner.offers[0];
  const security = useAppSelector(selectSecurity);
  const profile = security.profile;
  const isAdmin = security.ui.isAdmin;
  const address = security.ui.address;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSent, setIsSent] = useState<boolean>(false);

  const initialValues: ServiceLeadFormValues = {
    contactName: [profile?.firstName, profile?.lastName].filter(Boolean).join(' '),
    contactEmail: profile?.email ?? '',
    contactPhone: profile?.phone ?? '',
    message: '',
    answers: {},
    attachments: null,
    consent: false,
  };

  const formik = useFormik<ServiceLeadFormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (!offer) return;
      try {
        await createServiceLead(offer.slug, values, preview);
        setIsSent(true);
      } catch {
        dispatch(setFlashMessage({ message: t('Form.error', namespace), type: 'error' }));
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const toggleAnswer = (fieldName: string, option: string, checked: boolean) => {
    const current = formik.values.answers[fieldName];
    const list = Array.isArray(current) ? current : [];
    const next = checked ? [...list, option] : list.filter((item) => item !== option);
    formik.setFieldValue('answers', { ...formik.values.answers, [fieldName]: next });
  };

  const setTextAnswer = (fieldName: string, value: string) => {
    formik.setFieldValue('answers', { ...formik.values.answers, [fieldName]: value });
  };

  // Demo: poptávka se nesmí odeslat (návštěvník není skutečný zákazník, lead by šel partnerovi) — platí pro všechny nabídky
  if (isDemo) {
    return (
      <div className="ServiceLeadForm ServiceLeadForm--sent">
        <div className="ServiceLeadForm__sentIcon ServiceLeadForm__sentIcon--demo">
          <Icon name="info" />
        </div>
        <Text as="h3" size="h3" color="Neutral800" align="center">
          {t('Form.demoTitle', namespace)}
        </Text>
        <Text as="p" size="body-medium" color="Neutral700" align="center">
          {t('Form.demoText', namespace)}
        </Text>
        <ButtonLink external to={`${APP_URL}${routes.otherServices}`} title={t('Form.demoToApp', namespace)} />
      </div>
    );
  }

  if (isSent) {
    return (
      <div className="ServiceLeadForm ServiceLeadForm--sent">
        <div className="ServiceLeadForm__sentIcon">
          <Icon name="checkmark" />
        </div>
        <Text as="h3" size="h3" color="Neutral800" align="center">
          {t('Form.successTitle', namespace)}
        </Text>
        <Text as="p" size="body-medium" color="Neutral700" align="center">
          {t('Form.successText', namespace)}
        </Text>
        <Button
          title={t('Form.successAgain', namespace)}
          variant="text"
          onClick={() => {
            formik.resetForm();
            setIsSent(false);
          }}
        />
      </div>
    );
  }

  const showInvalidSummary = formik.submitCount > 0 && !formik.isValid;

  if (!offer) return null;

  const selectOffer = (slug: string) => {
    setSelectedSlug(slug);
    // Odpovědi patří k polím konkrétní služby – při přepnutí začít čistě.
    formik.setFieldValue('answers', {});
  };

  return (
    <form className="ServiceLeadForm" onSubmit={formik.handleSubmit} noValidate>
      {/* Předseda poptává za dům (dům jde partnerovi automaticky); vlastník poptává sám za sebe – dům se mu neukazuje. */}
      {isAdmin && address && (
        <div className="ServiceLeadForm__building">
          <Icon name="building" />
          <Text as="span" size="body-small" color="Neutral700">
            {t('Form.adminBuilding', namespace)}: <strong>{address}</strong>
          </Text>
        </div>
      )}

      <Row>
        <Col size="6" className="mb-2">
          <TextField
            name="contactName"
            label={t('Form.Fields.contactName.label', namespace)}
            placeholder={t('Form.Fields.contactName.placeholder', namespace)}
            value={formik.values.contactName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={{ message: formik.errors.contactName, touched: formik.touched.contactName }}
            required
          />
        </Col>
        <Col size="6" className="mb-2">
          <TextField
            name="contactEmail"
            type="email"
            label={t('Form.Fields.contactEmail.label', namespace)}
            placeholder={t('Form.Fields.contactEmail.placeholder', namespace)}
            value={formik.values.contactEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={{ message: formik.errors.contactEmail, touched: formik.touched.contactEmail }}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col size="6" className="mb-2">
          <PhoneField
            name="contactPhone"
            label={t('Form.Fields.contactPhone.label', namespace)}
            placeholder={t('Form.Fields.contactPhone.placeholder', namespace)}
            value={formik.values.contactPhone}
            onChange={(phone) => {
              formik.setFieldValue('contactPhone', phone ?? '');
              formik.setFieldTouched('contactPhone', true, false);
            }}
            error={{ message: formik.errors.contactPhone, touched: formik.touched.contactPhone || formik.submitCount > 0 }}
            required
          />
        </Col>
      </Row>

      {partner.offers.length > 1 && (
        <div className="ServiceLeadForm__group mb-2">
          <Text as="div" size="body-small" weight="600" color="Neutral750" className="ServiceLeadForm__groupLabel">
            {t('Form.interest', namespace)}
          </Text>
          <div className="ServiceLeadForm__options">
            {partner.offers.map((item) => (
              <RadioField
                key={item.slug}
                name="interest"
                value={item.slug}
                label={item.title}
                checked={item.slug === selectedSlug}
                onChange={() => selectOffer(item.slug)}
              />
            ))}
          </div>
        </div>
      )}

      {offer.formFields.map((field) =>
        field.type === 'checkboxGroup' ? (
          <div key={field.name} className="ServiceLeadForm__group mb-2">
            <Text as="div" size="body-small" weight="600" color="Neutral750" className="ServiceLeadForm__groupLabel">
              {field.label}
            </Text>
            <div className="ServiceLeadForm__options">
              {field.options.map((option) => {
                const current = formik.values.answers[field.name];
                const checked = Array.isArray(current) && current.includes(option);
                return (
                  <CheckboxField
                    key={option}
                    name={`answers.${field.name}.${option}`}
                    label={option}
                    checked={checked}
                    onChange={(event) => toggleAnswer(field.name, option, event.target.checked)}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <TextField
            key={field.name}
            name={`answers.${field.name}`}
            label={field.label}
            placeholder={field.placeholder}
            value={typeof formik.values.answers[field.name] === 'string' ? (formik.values.answers[field.name] as string) : ''}
            onChange={(event) => setTextAnswer(field.name, event.target.value)}
            className="mb-2"
          />
        )
      )}

      <TextAreaField
        name="message"
        label={t('Form.Fields.message.label', namespace)}
        placeholder={t('Form.Fields.message.placeholder', namespace)}
        value={formik.values.message}
        onChange={formik.handleChange}
        className="mb-2"
      />

      {OFFERS_WITH_ATTACHMENTS.has(offer.slug) && (
        <>
          <FileField
            name="attachments"
            label={t('Form.Fields.attachments.label', namespace)}
            filesLabel={`${namespace}.Form.Fields.attachments.filesLabel`}
            multiple={true}
            accept="image/*,.pdf,.doc,.docx"
            reference={fileInputRef}
            formikValue={formik.values.attachments}
            onChange={(event) => {
              if (event && event.currentTarget && event.currentTarget.files) {
                formik.setFieldValue('attachments', event.currentTarget.files);
              }
            }}
          />
          <Text as="div" size="body-small" color="Neutral600" className="mb-3">
            {t('Form.Fields.attachments.hint', namespace)}
          </Text>
        </>
      )}

      <CheckboxField
        name="consent"
        checked={formik.values.consent}
        onChange={(event) => formik.setFieldValue('consent', event.target.checked)}
        label={consentText(partner)}
        error={{ message: formik.errors.consent, touched: formik.submitCount > 0 }}
        className="ServiceLeadForm__consent mb-3"
      />

      {showInvalidSummary && <FormMessage type="error" message={t('Form.invalid', namespace)} className="mb-2" />}

      <Button
        type="submit"
        title={formik.isSubmitting ? t('Form.submitting', namespace) : offer.ctaLabel || t('Form.submit', namespace)}
        disabled={formik.isSubmitting}
        loading={formik.isSubmitting}
        wide
      />
    </form>
  );
};

export default ServiceLeadForm;
